import { useFocusEffect } from '@react-navigation/native';
import type { DependencyList } from 'react';
import { useCallback, useRef } from 'react';
import { AppState } from 'react-native';
import AppStateHelper from 'src/presentation/helpers/AppStateHelper';

type Props = {
	onInit?: () => void;
	onFocussed?: () => void;
	onUnfocussed?: () => void;
	onForegrounded?: () => void;
	onBackgrounded?: () => void;
};

/**
 * Per-screen AppState hook.
 */
function useAppStateScreenListener(
	{ onInit, onFocussed, onUnfocussed, onForegrounded, onBackgrounded }: Props,
	deps?: DependencyList,
) {
	const appState = useRef(AppState.currentState);
	const isInitialized = useRef(false);

	useFocusEffect(
		useCallback(() => {
			const appStateChangedListener = AppState.addEventListener('change', (nextAppState) => {
				if (
					AppStateHelper.isForegrounded({
						current: appState.current,
						next: nextAppState,
					})
				) {
					onForegrounded?.();
				} else {
					onBackgrounded?.();
				}

				appState.current = nextAppState;
			});

			if (!isInitialized.current) {
				isInitialized.current = true;
				onInit?.();
			}

			onFocussed?.();

			return () => {
				onUnfocussed?.();
				appStateChangedListener.remove();
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, deps ?? []),
	);

	return null;
}

export default useAppStateScreenListener;
